import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "../../../store";
import Table from "../../../components/ui/table/Table";
import MainNavigation from "../../../components/navigation/MainNavigation";
import { fetchImportTypes, fetchImports, deleteImport } from "../../../store/slices/imports";
import { fetchBrands } from "../../../store/slices/brands";
import Button from "../../../components/ui/common/Button";
import { EditIcon, DeleteIcon, PlusIcon } from "../../../icons/Icons";
import { CircleLoader } from "../../../icons/Loaders";
import Modal from "../../../components/ui/modal/Modal";
import Select from "../../../components/ui/select/Select";
import EditImportModal from "../../../components/modals/EditImportModal/EditImportModal";
import CollapsibleCell from "../../../components/ui/table/CollapsibleCell";
import EditDisplayedColumnsModal from "../EditDisplayedColumnsModal";
import Api from "../../../store/api/Api";
import cronstrue from "cronstrue";
import cronParser from "cron-parser";
import "../Admin.scss";
import "./imports.scss";

const Imports = () => {
  const dispatch = useAppDispatch();
  const importsNavPath = "/admin/imports";
  const [editingColumns, setEditingColumns] = useState(false);
  const imports = useSelector((state: any) => state.imports.data);
  const isLoading = useSelector((state: any) => state.imports.isLoading);
  const brands = useSelector((state: any) => state.brands.data);
  const currentBrand = useSelector((state: any) => state.currentBrand.data);
  const [editingImport, setEditingImport] = useState(false);
  const [importEditData, setImportEditData] = useState({});
  const [creatingNewImport, setCreatingNewImport] = useState(false);
  const [selectingTemplateImport, setSelectingTemplateImport] = useState(false);
  const [templateImports, setTemplateImports] = useState<any[]>([]);
  const [loadingTemplateImports, setLoadingTemplateImports] = useState(false);
  const [templateImportError, setTemplateImportError] = useState<string | null>(null);
  const [selectedTemplateBrand, setSelectedTemplateBrand] = useState<string>("0templateimportsbrand");
  const [selectedTemplateImport, setSelectedTemplateImport] = useState<string>("");
  const api = useMemo(() => new Api(), []);

  useEffect(() => {
    document.title = "Admin - Imports - Smartocto real-time";
    document.body.dataset.titleReady = "true";
  }, []);

  const navigate = useNavigate();
  const { id } = useParams();

  const handleEdit = useCallback((id) => {
    const importData = imports.find((item) => item.id === id);
    if (importData) {
      setEditingImport(true);
      setCreatingNewImport(false);
      setImportEditData(importData);
    }
  }, [imports]);

  useEffect(() => {
    // if the id is found in the imports list, open the modal
    if (id && imports.length > 0 && imports.find((item) => item.id === id) && (!editingImport || (importEditData.hasOwnProperty("id") && importEditData["id"] && importEditData["id"] !== id))) {
      handleEdit(id);
    }
    // navigate to the base path if the id is not found in the imports list
    if (id && imports.length > 0 && imports.find((item) => item.id === id) === undefined && !creatingNewImport) {
      navigate(importsNavPath);
    }
  }, [id, imports, editingImport, handleEdit, importEditData, navigate, creatingNewImport]);

  useEffect(() => {
    if (!id && importEditData["id"] && !creatingNewImport) {
      navigate(`${importsNavPath}/${importEditData["id"]}`);
    }
  }, [id, navigate, importEditData, creatingNewImport]);

  useEffect(() => {
    if (!id && Object.keys(importEditData).length === 0 && !creatingNewImport && !selectingTemplateImport) {
      navigate(importsNavPath);
    }
  }, [id, navigate, importEditData, creatingNewImport, selectingTemplateImport]);
  const handleDelete = (id) => {
    if (window.confirm("Are you sure")) {
      dispatch(deleteImport(id));
    }
  };

  const DEFAULT_TEMPLATE_BRAND_ID = "0templateimportsbrand";

  const loadTemplateImports = useCallback((brandId: string) => {
    setLoadingTemplateImports(true);
    setTemplateImportError(null);
    api.getImportsByBrandId(brandId)
      .then((response: any) => {
        setTemplateImports(response || []);
      })
      .catch((err: any) => {
        console.error("Failed to load template imports:", err);
        setTemplateImportError("Failed to load template imports.");
        setTemplateImports([]);
      })
      .finally(() => {
        setLoadingTemplateImports(false);
      });
  }, [api]);

  const handleSetNewImportCreation = () => {
    setSelectingTemplateImport(true);
    setSelectedTemplateImport("");
    setSelectedTemplateBrand(DEFAULT_TEMPLATE_BRAND_ID);
    loadTemplateImports(DEFAULT_TEMPLATE_BRAND_ID);
  };

  const handleTemplateImportSelected = () => {
    if (!selectedTemplateImport) return;
    const templateImport = templateImports.find((item) => item.id === selectedTemplateImport);
    if (!templateImport) return;

    const duplicatedImport = {
      ...templateImport,
      id: undefined,
      brandId: currentBrand?.domain || "",
      active: false,
    };
    delete duplicatedImport.id;

    setImportEditData(duplicatedImport);
    setCreatingNewImport(true);
    setEditingImport(true);
    setSelectingTemplateImport(false);
    setSelectedTemplateImport("");
  };

  const handleTemplateSelectionClose = () => {
    setSelectingTemplateImport(false);
    setSelectedTemplateImport("");
    setSelectedTemplateBrand(DEFAULT_TEMPLATE_BRAND_ID);
    setTemplateImportError(null);
  };

  const columns = useMemo(() => [
    { title: "id", label: "id" },
    { title: "insertMethod", label: "Insert Method" },
    { title: "index", label: "Index" },
    { title: "active", label: "Active" },
    { title: "type", label: "Type" },
    { title: "cronTime", label: "Cron time" },
    { title: "intoType", label: "IntoType" },
    { title: "url", label: "URL" },
    { title: "brandId", label: "BrandId" },
    { title: "actions", label: "Actions" },
  ], []);

  const preparedColumns = useMemo(() => {
    try {
      const tablesConfig = JSON.parse(localStorage.tablesConfig);
      const excludedColumns = tablesConfig.imports;
      return columns.filter((obj) => !excludedColumns.includes(obj.label));
    } catch {
      return columns;
    }
  }, [columns]);

  const dataWithActions = imports.map((e, i) => {
    return {
      ...e,
      sort: {
        id: e.id,
        imapLabel: e.imapLabel || '',
        insertMethod: e?.insertMethod,
        index: e?.index,
        active: e?.active,
        type: e?.type,
        intoType: e?.intoType,
        url: e?.url,
        brandId: e?.brandId,
      },
      imapLabel: e.imapLabel || (e.type === 'newsletter' ? e.username : ''),
      insertMethod: e.insertMethod || "",
      index: e.index || "",
      active: e.active ? "yes" : "no",
      intoType: e.intoType || "",
      cronTime: (
        <div>
          {e.cronTime? cronstrue.toString(e.cronTime) : ""}
          <br />
          <b> {!e.cronTime? <em>No cron time set for this import</em> : "Next iteration"}  </b>
          <br />
          {e.cronTime? cronParser.parseExpression(e.cronTime).next().toString(): ""}
        </div>
      ),
      url: (
        <div className="imports-url-container">
          <CollapsibleCell>{e.url || ""}</CollapsibleCell>
        </div>
      ),

      actions: (
        <div style={{ display: "flex" }}>
          <Button
            variant="warning"
            data-testid={`editButton${i}`}
            size="small"
            style={{ marginLeft: "10px", marginRight: "10px" }}
            onClick={() => {
              handleEdit(e.id);
              setEditingImport(true);
            }}
          >
            <EditIcon />
          </Button>

          <Button.Feedback
            variant="danger"
            data-testid={`deleteButton${i}`}
            size="small"
            onClick={() => {
              handleDelete(e.id);
            }}
          >
            <DeleteIcon />
          </Button.Feedback>
        </div>
      ),
    };
  });

  useEffect(() => {
    dispatch(fetchImports());
    dispatch(fetchImportTypes());
    dispatch(fetchBrands());
  }, [dispatch]);

  const handleModalClose = () => {
    setEditingImport(false);
    setCreatingNewImport(false);
    setImportEditData({});
    // Ensure navigation to base path after the modal is closed
    navigate(importsNavPath);
  };

  return (
    <>
      <Modal isOpen={editingImport} onClose={handleModalClose}>
        <EditImportModal 
          data={importEditData} 
          onClose={handleModalClose}
          newImport={creatingNewImport}
        />
      </Modal>
      <Modal
        isOpen={editingColumns}
        onClose={() => {
          setEditingColumns(false);
        }}
      >
        <EditDisplayedColumnsModal tableTitle="imports" columns={columns} />
      </Modal>
      <Modal isOpen={selectingTemplateImport} onClose={handleTemplateSelectionClose}>
        <div className="imports-modal-wrapper template-import-selection-modal">
          <h1 className="imports-modal-title">Add new import configuration</h1>
          <div className="imports-modal-container">
            <div className="column">
              <div className="drop-select">
                <label htmlFor="templateBrand">Select a brand to copy configurations from</label>
                <Select
                  options={useMemo(() => {
                    const options = brands.map((brand) => ({
                      value: brand.id,
                      label: brand.brandName || brand.id,
                    }));
                    const hasTemplateBrand = options.some((option) => option.value === DEFAULT_TEMPLATE_BRAND_ID);
                    if (!hasTemplateBrand) {
                      options.unshift({
                        value: DEFAULT_TEMPLATE_BRAND_ID,
                        label: DEFAULT_TEMPLATE_BRAND_ID,
                      });
                    }
                    return options;
                  }, [brands])}
                  selectedOption={selectedTemplateBrand}
                  setSelectedOption={(value: string) => {
                    setSelectedTemplateBrand(value);
                    setSelectedTemplateImport("");
                    loadTemplateImports(value);
                  }}
                  styling={{
                    selectOptionsContainerClassName: "options-dropdown",
                  }}
                  fullWidth
                  search
                />
              </div>
              <div className="drop-select">
                <label htmlFor="templateImport">Select a configuration to copy</label>
                {loadingTemplateImports ? (
                  <p>Loading configurations...</p>
                ) : templateImportError ? (
                  <div className="error-message">{templateImportError}</div>
                ) : (
                  <Select
                    options={[...templateImports]
                      .sort((a, b) => a.type.localeCompare(b.type))
                      .filter((item) => (!item.type.startsWith('tentacle') && !item.type.endsWith('Clicks')) || item.type === 'tentaclePage2') // exclude all types starting with tentacle and ending with Clicks except tentaclePage2
                      .map((item) => ({
                        value: item.id,
                        label: `${item.type}${item.intoType ? ` (${item.intoType})` : ""} - ${item.id}`,
                      }))}
                    selectedOption={selectedTemplateImport}
                    setSelectedOption={(value: string) => {
                      setSelectedTemplateImport(value);
                    }}
                    styling={{
                      selectOptionsContainerClassName: "options-dropdown",
                    }}
                    fullWidth
                    search
                  />
                )}
              </div>
            </div>
          </div>
          <div className="sub-modal-actions">
            <Button
              variant="danger"
              onClick={() => {
                handleTemplateSelectionClose();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              disabled={!selectedTemplateImport || loadingTemplateImports}
              onClick={() => {
                handleTemplateImportSelected();
              }}
            >
              Add configuration
            </Button>
          </div>
        </div>
      </Modal>
      <MainNavigation />
      <div className="page-container-no-side-nav">
        <h1 className="admin-page-title">Imports{currentBrand?.brandName ? ` - ${currentBrand.brandName}` : ''}</h1>

        <div className="admin-page-header-btns">
          <Button
            style={{ marginLeft: 20, padding: "6px 8px" }}
            variant="success"
            onClick={() => {
              handleSetNewImportCreation();
            }}
          >
            <PlusIcon width="16px" height="16px" />
          </Button>
          <Button
            style={{ marginLeft: 20 }}
            title="Edit Imports columns"
            variant="success"
            onClick={() => {
              setEditingColumns(true);
            }}
          >
            <EditIcon /> Edit Columns
          </Button>
        </div>

        {!isLoading ? (
          <div className="table-container">
            <Table showRowNumbers={true} rowData={dataWithActions} columnsData={preparedColumns} />
          </div>
        ) : (
          <div className="loader-container">
            <CircleLoader />
          </div>
        )}
      </div>
    </>
  );
};

export default Imports;
